import { formatTime } from '@erp/shared';
import { EyeOff } from 'lucide-react';
import { useEffect, useState } from 'react';

import { api, errorMessage } from '../../lib/api.ts';
import { reloadAt } from '../../lib/navigation.ts';
import type { Session } from '../../lib/session.ts';
import { useToast } from '../../lib/toast.tsx';
import { Button } from '../ui/button.tsx';

/**
 * Always visible while an administrator acts as someone else: who, why and until
 * when, with a way out. Warns 5 minutes before it ends.
 */
export function ImpersonationBanner({ session }: { session: Session }) {
  const notify = useToast();
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const impersonation = session.impersonation;

  useEffect(() => {
    const timer = setInterval(() => {
      setNow(Date.now());
    }, 30_000);
    return () => {
      clearInterval(timer);
    };
  }, []);

  if (!impersonation) return null;
  const minutesLeft = Math.max(0, Math.ceil((Date.parse(impersonation.expiresAt) - now) / 60_000));

  const end = async () => {
    setBusy(true);
    try {
      await api('/impersonation/end', { method: 'POST' });
      // Back to the administrator's own session: reload the app with it.
      reloadAt('/admin/usuarios');
    } catch (err) {
      notify(errorMessage(err), 'error');
      setBusy(false);
    }
  };

  return (
    <div role="status" className="sticky top-0 z-40 bg-warning text-warning-foreground">
      <div className="mx-auto flex max-w-6xl flex-col gap-2 px-4 py-2 text-sm sm:flex-row sm:items-center">
        <EyeOff className="hidden size-4 shrink-0 sm:block" aria-hidden="true" />
        <p className="flex-1">
          <strong>
            {impersonation.by.displayName ?? 'Un administrador'} está actuando como{' '}
            {session.user.fullName}.
          </strong>{' '}
          Motivo: {impersonation.reason}. Termina a las{' '}
          {formatTime(impersonation.expiresAt, session.tenant.timezone)}
          {minutesLeft <= 5 && <span className="font-semibold"> (quedan {minutesLeft} min)</span>}.
        </p>
        <Button size="sm" variant="secondary" loading={busy} onClick={() => void end()}>
          Terminar y volver a mi cuenta
        </Button>
      </div>
    </div>
  );
}
